import { useContext, useMemo, useState } from "react"
import PageSubtitle from "../../_general/PageSubtitle"
import SensorLocationPicker from "./SensorLocationPicker"
import PitchTelemetry from "../../../context/covermaster/PitchTelemetry"
import CovermasterContext from "../../../context/covermaster/Covermaster"
import { css } from "@emotion/react"
import SwitcherSimple from "../../_control/SwitcherSimple"
import Orientation from "../../test/Orientation"
import { colors } from "shared-ui"
import { formatDistanceToNow, fromUnixTime } from "date-fns"
import LocaleContext from "../../../context/Locale"
import useRawState from "../../../hooks/useRawState"
import Loader from "../../_general/Loader"
import EventZones from "../../events/EventZones"
import { useIntl } from "react-intl"

export default function PitchSensorsSection({ className, ...props }) {
    const { formatMessage } = useIntl()
    const { datefnsLocale } = useContext(LocaleContext)
    const { devices } = useContext(CovermasterContext)
    const {
        titleMessage,
        selectedPosition,
        handleSelectPosition,
        isCoverOn,
        pitch,
        pitchBg,
        positionNames,
        currentCoverEvent,
    } = useContext(PitchTelemetry)

    const [showBatteryOnGrid, setShowBatteryOnGrid] = useState(true)

    const pitchDevices = useMemo(() => {
        return devices?.filter(d => d.latestDevicePlacementEvent?.pitch?.id === pitch.id)
    }, [devices, pitch])

    const selectedDeviceInfo = useMemo(() => {
        const selected = pitchDevices?.find(d => d.latestDevicePlacementEvent?.posX === selectedPosition?.posX && d.latestDevicePlacementEvent?.posY === selectedPosition?.posY)

        const x = selected?.latestDevicePlacementEvent?.posX
        const y = selected?.latestDevicePlacementEvent?.posY

        if (!x || !y) return

        const timestamp = selected.latestDeviceStatusEvent?.timestamp
        const neverMessage = formatMessage({ id: 'never' })

        const formattedStatusTime = timestamp ? formatDistanceToNow(
            fromUnixTime(selected.latestDeviceStatusEvent?.timestamp),
            { addSuffix: true, locale: datefnsLocale }
        ) : neverMessage

        const formattedPlacementTime = timestamp ? formatDistanceToNow(
            fromUnixTime(selected.latestDevicePlacementEvent?.timestamp),
            { addSuffix: true, locale: datefnsLocale }
        ) : neverMessage

        return {
            name: selected.name,
            owner: selected.owner,
            battery: selected.latestDeviceStatusEvent?.batteryLevel,
            statusTime: formattedStatusTime,
            placementTime: formattedPlacementTime,
            posX: x,
            posY: y,
            placementMode: selected.latestDevicePlacementEvent?.deviceMode,
            positionName: positionNames[`${x}${y}`],
            deviceType: selected.deviceType,
            devEUI: selected.devEUI,
            appKey: selected.appKey,
        }

    }, [selectedPosition, pitchDevices])

    return <div css={className}>
        <PageSubtitle css={css`grid-column: span 2; margin: 0; font-size: 1.5em;`}>
            {titleMessage}
        </PageSubtitle>

        <div>
            <SensorLocationPicker
                posX={selectedPosition?.posX ?? null}
                posY={selectedPosition?.posY ?? null}
                handleSetCoordinates={(x, y) => {
                    handleSelectPosition(x !== null && y !== null ? { posX: x, posY: y } : undefined)
                }}
                otherDevices={pitchDevices}
                pickAnyPosition={true}
                showGrid={false}
                showPitchBg={true}
                pitchBg={pitchBg}
                showBattery={showBatteryOnGrid}
                showPlacementLabel={true}
                underLay={currentCoverEvent ?
                    <EventZones
                        event={currentCoverEvent}
                        hideBackground={true}
                        showGrid={false}
                        css={css` opacity: 0.4;`}
                    />
                    :
                    <></>
                }
            />
            <div css={telemetryStyle.belowGrid}>
                <Orientation
                    heading={pitch?.orientation}
                    css={telemetryStyle.compass}
                />
                <SwitcherSimple
                    checked={showBatteryOnGrid}
                    onClick={() => setShowBatteryOnGrid(curr => !curr)}
                    checkedLabel={formatMessage({ id: 'battery' })}
                    unCheckedLabel={formatMessage({ id: 'placement' })}
                    buttonCenter={true}
                    css={telemetryStyle.toggle}
                />
            </div>
        </div>
        {selectedDeviceInfo ? <div>
            <div css={css`
                display: grid; 
                grid-auto-rows: min-content;
                gap: 4px;
                margin-bottom: 1em;
                
                > div {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.5em 1em;
                    background: rgba(0,0,0,0.1);
                    color: ${colors.soft};
                    height: min-content;
                    gap: 1em;
                    word-break: break-all;
                }
            `}>
                <div>
                    {formatMessage({ id: 'name' })} <span> {selectedDeviceInfo.name} </span>
                </div>
                <div>
                    DEV EUI <span> {selectedDeviceInfo.devEUI} </span>
                </div>
                <div>
                    App Key <span> {selectedDeviceInfo.appKey} </span>
                </div>
                <Owner ownerIri={selectedDeviceInfo.owner} />

                <div>
                    {formatMessage({ id: 'battery' })}  <span> {selectedDeviceInfo.battery ?? 'unknown '}%</span>
                </div>
                <div>
                    {formatMessage({ id: 'mode' })}  <span> {selectedDeviceInfo.placementMode} </span>
                </div>
                <div >
                    {formatMessage({ id: 'position' })} <span css={css`text-transform: lowercase;`}> {selectedDeviceInfo.positionName} </span>
                </div>
                <div>
                    {formatMessage({ id: 'sensorType' })} <span> {selectedDeviceInfo.deviceType} </span>
                </div>
                <div>
                    {formatMessage({ id: 'statusUpdated' })}  <span>{selectedDeviceInfo.statusTime} </span>
                </div>
                <div>
                    {formatMessage({ id: 'placementUpdated' })} <span>{selectedDeviceInfo.placementTime}</span>
                </div>

            </div>

        </div>
            :
            <PageSubtitle css={css`font-size: 1.5em;`}>
                {formatMessage({ id: 'selectASensor' })}
            </PageSubtitle>
        }

    </div>
}

function Owner({ ownerIri, ...props }) {
    const { formatMessage } = useIntl()
    const { data: owner, isBusy: ownerBusy } = useRawState(ownerIri)

    return <div>
        {formatMessage({ id: 'owner' })}  <span>
            {ownerBusy ? <Loader size='1em' /> :
                (owner?.email ?? owner?.name) ?? formatMessage({ id: 'noOwner' })
            }
        </span>
    </div>
}

const telemetryStyle = {
    belowGrid: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: rgba(0,0,0,0.1); 
        padding: 0.3em 1em;  
            margin-top: 0.5em;
            gap: 1em;
    `,
    compass: css`
        background: transparent; 
        width: min-content;
        height: min-content; 
    `,
    toggle: css`
        border-radius: 5px;
        width: max-content;
        white-space: nowrap;
        place-self: center;
        height: 100%;
        padding-left: 1em;
        padding-right: 1em;
        height: min-content;
    `,
}